/* alm-auction.js — A Laughing Matter Theme
   Auction display, bid modal, bid placement
   Bids stored in Shopify Metafields via Admin API proxy endpoint
   ================================================== */

/* ── Auction state ── */
let auctionFilter = 'all';
let activeBidId   = null;

/* ── Helpers (shared with alm-ui.js via window scope) ── */
function auctionStatus(endTime) {
  if (!endTime) return null;
  const now = Date.now();
  const end = parseInt(endTime);
  if (end <= now) return 'ended';
  return 'live';
}
function getBids(pid) {
  // In production: fetch from /apps/alm-bids?product_id=pid
  // For now: localStorage keyed bids (same as prototype)
  return JSON.parse(localStorage.getItem('alm_bids_' + pid) || '[]');
}
function saveBid(pid, bid) {
  const bids = getBids(pid);
  bids.unshift(bid);
  bids.sort((a,b) => b.amount - a.amount);
  localStorage.setItem('alm_bids_' + pid, JSON.stringify(bids));
}
function getTopBid(pid)  { const b = getBids(pid); return b.length ? b[0] : null; }
function getCurrentPrice(product) {
  const top = getTopBid(product.id);
  const startBid = parseInt(product.startBid || product.price || 0);
  return top ? top.amount : startBid;
}
function timeLeft(endTime) {
  const diff = Math.max(0, parseInt(endTime) - Date.now());
  return { d:Math.floor(diff/86400000), h:Math.floor((diff%86400000)/3600000), m:Math.floor((diff%3600000)/60000), s:Math.floor((diff%60000)/1000), total:diff };
}
function fmtCountdown(endTime) {
  const {d,h,m,s} = timeLeft(endTime);
  if (d>0) return `${d}d ${h}h`;
  if (h>0) return `${h}h ${m}m`;
  if (m>0) return `${m}m ${s}s`;
  return `${s}s`;
}
function anon(name) {
  if (!name) return 'Anonymous';
  const p = name.split(' ');
  return p[0] + (p[1] ? ' ' + p[1][0] + '.' : '');
}
function relTime(ts) {
  const d = Date.now() - ts;
  if (d < 60000) return 'just now';
  if (d < 3600000) return Math.floor(d/60000) + 'm ago';
  if (d < 86400000) return Math.floor(d/3600000) + 'h ago';
  return Math.floor(d/86400000) + 'd ago';
}

/* Countdown tick */
function startCountdowns() {
  clearInterval(window._cd);
  window._cd = setInterval(() => {
    document.querySelectorAll('[data-countdown]').forEach(el => {
      const end = parseInt(el.dataset.countdown);
      if (!end) return;
      const {total,d,h,m,s} = timeLeft(end);
      el.textContent = total<=0 ? 'ENDED' : d>0 ? `${d}d ${h}h ${m}m` : h>0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    });
    const bmEnd = document.getElementById('bm-end-time');
    if (bmEnd) {
      const end = parseInt(bmEnd.dataset.end);
      if (end) {
        const {d,h,m,s,total} = timeLeft(end);
        document.querySelectorAll('.countdown-num').forEach((el,i) => {
          el.textContent = String(total<=0?0:[d,h,m,s][i]).padStart(2,'0');
        });
      }
    }
  }, 1000);
}

/* ── Open bid modal ── */
function openBidModal(productId) {
  /* Reads auction data from card's data attributes, falls back to DOM */
  const card = document.querySelector(`[data-product-id="${productId}"]`);
  if (!card) return;
  const endTime  = card.dataset.auctionEnd;
  const startBid = parseInt(card.dataset.auctionStartBid || 0);
  const reserve  = parseInt(card.dataset.auctionReserve  || 0) || null;
  const buyNow   = parseInt(card.dataset.auctionBuyNow   || 0) || null;
  const incr     = parseInt(card.dataset.auctionIncrement || 5);
  const title    = card.querySelector('.product-name')?.textContent || '';
  const imgEl    = card.querySelector('.product-img img');
  const emoji    = card.querySelector('.product-img span')?.textContent || '👕';
  const ast      = auctionStatus(endTime);
  const bids     = getBids(productId);
  const top      = getTopBid(productId);
  const cur      = top ? top.amount : startBid;
  const minNext  = cur + incr;
  const isLive   = ast === 'live';
  const isEnded  = ast === 'ended';
  activeBidId    = productId;

  /* Image side */
  document.getElementById('bm-img').innerHTML = imgEl
    ? `<img src="${imgEl.src}" alt="${title}" style="width:100%;height:100%;min-height:420px;object-fit:cover;">`
    : `<span style="font-size:7rem;">${emoji}</span>`;

  /* Countdown HTML */
  const {d,h,m,s} = timeLeft(endTime);
  const cdHtml = isLive ? `
    <div id="bm-end-time" data-end="${endTime}" style="display:none;"></div>
    <div class="big-countdown">
      <div class="countdown-unit"><span class="countdown-num">${String(d).padStart(2,'0')}</span><span class="countdown-unit-lbl">Days</span></div>
      <span class="countdown-sep">:</span>
      <div class="countdown-unit"><span class="countdown-num">${String(h).padStart(2,'0')}</span><span class="countdown-unit-lbl">Hrs</span></div>
      <span class="countdown-sep">:</span>
      <div class="countdown-unit"><span class="countdown-num">${String(m).padStart(2,'0')}</span><span class="countdown-unit-lbl">Min</span></div>
      <span class="countdown-sep">:</span>
      <div class="countdown-unit"><span class="countdown-num">${String(s).padStart(2,'0')}</span><span class="countdown-unit-lbl">Sec</span></div>
    </div>` : '';

  const bidInput = isLive ? `
    <div>
      <div class="bid-input-row">
        <div class="bid-input-wrap"><input class="bid-input" type="number" id="bid-amount" value="${minNext}" min="${minNext}" step="${incr}"></div>
        <button class="btn btn-bid" style="padding:0.7rem 1.2rem;font-size:0.75rem;letter-spacing:0.1em;" onclick="ALMAuction.placeBid('${productId}')">BID →</button>
      </div>
      <div class="bid-presets">
        <span style="font-family:'Space Mono',monospace;font-size:0.58rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--gray);align-self:center;">Quick:</span>
        ${[minNext, minNext+incr, minNext+incr*3, minNext+incr*6].map(v => `<button class="bid-preset" onclick="document.getElementById('bid-amount').value=${v}">+$${v-cur}</button>`).join('')}
      </div>
      ${buyNow ? `<button class="btn btn-ink" style="width:100%;padding:0.7rem;justify-content:center;margin-top:0.6rem;font-size:0.72rem;letter-spacing:0.1em;" onclick="ALMAuction.buyNow('${productId}')">BUY NOW — $${buyNow}</button>` : ''}
      <div class="bid-msg" id="bid-msg"></div>
    </div>`
  : isEnded
    ? `<div style="background:var(--near);border:2px solid var(--ink);padding:1rem;text-align:center;">${top ? `<div style="font-family:'Bebas Neue',sans-serif;font-size:1.8rem;">Won by ${anon(top.name)}</div><div style="font-family:'Space Mono',monospace;font-size:0.68rem;text-transform:uppercase;color:var(--gray);">Final: $${top.amount}</div>` : `<div style="font-family:'Space Mono',monospace;font-size:0.7rem;text-transform:uppercase;color:var(--gray);">No bids placed</div>`}</div>`
    : `<div style="background:var(--near);border:2px solid var(--ink);padding:1rem;text-align:center;font-family:'Space Mono',monospace;font-size:0.7rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--gray);">Bidding opens when auction goes live</div>`;

  const hist = `
    <div class="bid-history">
      <div class="bid-history-title">Bid History</div>
      ${bids.length
        ? bids.map((b,i) => `<div class="bid-row ${i===0?'top-bid':''}"><div class="bid-row-rank ${i===0?'rank-1':''}"> ${i===0?'#1':i+1}</div><div class="bid-row-name">${anon(b.name)}</div><div class="bid-row-amount">$${b.amount}</div><div class="bid-row-time">${relTime(b.ts)}</div></div>`).join('')
        : '<div class="bid-history-empty">No bids yet. Be first.</div>'}
    </div>`;

  const sbClass = isLive ? 'live' : isEnded ? 'ended' : 'upcoming';
  document.getElementById('bm-panel').innerHTML = `
    <div class="modal-category">Auction Lot</div>
    <div class="modal-title">${title}</div>
    <div class="auction-status-bar ${sbClass}">
      <div class="asb-icon">${isLive?'⚡':isEnded?'🏆':'⏳'}</div>
      <div><div class="asb-title">${isLive?'LIVE AUCTION':isEnded?'AUCTION ENDED':'UPCOMING'}</div>
      <div class="asb-sub">${isLive?`Ends in <span data-countdown="${endTime}">...</span>`:isEnded?`${bids.length} bids placed`:'Opens soon'}</div></div>
    </div>
    ${cdHtml}
    <div class="current-bid-display">
      <div class="cbd-label">${top?'Current Bid':'Starting Bid'}</div>
      <div class="cbd-amount">$${cur}</div>
      ${top?`<div class="cbd-bidder">Leading: ${anon(top.name)}</div>`:''}
      <div class="cbd-count">${bids.length} bid${bids.length!==1?'s':''} · ${ast||''}</div>
    </div>
    <div class="starting-info">
      <div class="info-chip"><label>Start</label><span>$${startBid}</span></div>
      ${reserve?`<div class="info-chip"><label>Reserve</label><span>$${reserve}</span></div>`:''}
      ${buyNow?`<div class="info-chip"><label>Buy Now</label><span>$${buyNow}</span></div>`:''}
    </div>
    ${bidInput}
    ${hist}`;

  document.getElementById('bid-modal').classList.add('open');
  startCountdowns();
}

/* ── ALMAuction namespace ── */
window.ALMAuction = {

  openBidModal,

  async placeBid(productId) {
    const msgEl = document.getElementById('bid-msg');
    const customer = window.theme?.customerId;
    if (!customer) {
      if (msgEl) msgEl.innerHTML = '<div class="bid-msg err">Log in to place a bid.</div>';
      setTimeout(() => openAuth(window.location.pathname), 700);
      return;
    }
    const endTime = document.querySelector(`[data-product-id="${productId}"]`)?.dataset.auctionEnd;
    if (auctionStatus(endTime) !== 'live') {
      if (msgEl) msgEl.innerHTML = '<div class="bid-msg err">Auction not live.</div>';
      return;
    }
    const amount = parseFloat(document.getElementById('bid-amount').value);
    const cur    = getCurrentPrice({ id: productId, startBid: parseInt(document.querySelector(`[data-product-id="${productId}"]`)?.dataset.auctionStartBid||0) });
    const incr   = parseInt(document.querySelector(`[data-product-id="${productId}"]`)?.dataset.auctionIncrement||5);
    const min    = cur + incr;
    if (isNaN(amount) || amount < min) {
      if (msgEl) msgEl.innerHTML = `<div class="bid-msg err">Minimum bid: $${min}</div>`;
      return;
    }
    const existing = getBids(productId);
    if (existing[0]?.userId === String(customer)) {
      if (msgEl) msgEl.innerHTML = '<div class="bid-msg info">You are already leading.</div>';
      return;
    }
    const bid = { userId: String(customer), name: window.theme.customerName || 'Bidder', amount, ts: Date.now() };
    saveBid(productId, bid);
    if (msgEl) msgEl.innerHTML = `<div class="bid-msg ok">✓ Bid of $${amount} placed! You're winning.</div>`;
    setTimeout(() => openBidModal(productId), 400);
    ALMUtils.toast(`Bid $${amount} placed!`);
  },

  buyNow(productId) {
    const buyNow = parseInt(document.querySelector(`[data-product-id="${productId}"]`)?.dataset.auctionBuyNow||0);
    if (!buyNow) return;
    const customer = window.theme?.customerId;
    if (!customer) { openAuth(window.location.pathname); return; }
    if (!confirm(`Buy now for $${buyNow}?`)) return;
    const bid = { userId: String(customer), name: window.theme.customerName || 'Buyer', amount: buyNow, ts: Date.now(), buyNow: true };
    saveBid(productId, bid);
    document.getElementById('bid-modal').classList.remove('open');
    document.getElementById('em-icon').textContent = '🌍';
    document.getElementById('em-title').textContent = `Congrats, ${window.theme.customerName || 'Winner'}!`;
    document.getElementById('em-body').innerHTML = `You copped this lot for <strong>$${buyNow}</strong>. We'll be in touch shortly.`;
    document.getElementById('ended-modal').classList.add('open');
    ALMUtils.toast(`Copped for $${buyNow}!`);
  }
};
